import moment from "moment";
import { isEqual } from "lodash";
import { DATE_FORMAT_ISO } from "../../thor.const";
import { thor } from "../../typings/thor";

enum SlotType {
    Sale = "sale"
}

export class SlotController {
    public static $inject: string[] = ["$scope"];

    // binded
    public slots: thor.ExhibitionSlot[] = [];
    public limit: number;
    public type: SlotType;
    public isDisabled: boolean;

    public currentModifyingSlotIndex: number = null;
    public isSlotFormDisplayed: boolean = false;
    public dateFrom;
    public slotDateFrom;
    public slotDateFromTime;
    public slotDateToTime;
    public slotForm: ng.IFormController;
    public minTimeFrom;
    public maxTimeFrom;
    public minTimeTo;
    public minSlotDateFrom;
    public slotDateFromIsDisabled: boolean;

    constructor($scope: ng.IScope) {
        $scope.$watch(
            () => this.slotDateFromTime,
            (newDateFrom: Date) => {
                if (!newDateFrom) {
                    return;
                }

                const momentFrom = moment(newDateFrom);

                // Replicate the date change
                // If the slotDateToTime is invalid (because it's anterior compare to slotDateFrom) it becomes undefined
                let momentTo = this.slotDateToTime === undefined ?
                    momentFrom.add(1, "hours") :
                    moment(this.slotDateToTime).set({
                        "year": momentFrom.get("year"),
                        "month": momentFrom.get("month"),
                        "date": momentFrom.get("date")
                    });

                // Ensure dateTo is always after dateFrom
                const duration = momentTo.diff(momentFrom, "minutes");
                if (duration <= 0) {
                    momentTo = moment(momentFrom).add(1, "hours");
                }

                this.slotDateToTime = momentTo.toDate();
            }
        );

        $scope.$watch(
            () => this.slotDateToTime,
            (newDateTo: Date) => {
                if (this.isSlotFormDisplayed) {
                    this.slotForm.timeTo.$setValidity(
                        "unequalTime",
                        moment(newDateTo).diff(moment(this.slotDateFromTime), "minutes") !== 0
                    );
                }
            }
        );

        $scope.$watch(
            () => this.slotDateFrom,
            (newSlotDateFrom: Date, oldSlotDateFrom: Date) => {
                if (newSlotDateFrom !== oldSlotDateFrom) {
                    const slotDateFromTime = moment(this.slotDateFromTime);
                    const slotDateToTime = moment(this.slotDateToTime);
                    this.slotDateFromTime = moment(newSlotDateFrom)
                        .hour(slotDateFromTime.get("hour"))
                        .minute(slotDateFromTime.get("minutes")).toDate();
                    this.slotDateToTime = moment(this.slotDateFromTime)
                        .hour(slotDateToTime.get("hour"))
                        .minute(slotDateToTime.get("minutes")).toDate();
                }
            }
        );

        $scope.$watch(
            () => this.slotDateFromTime,
            () => {
                if (this.type === SlotType.Sale) {
                    const isDateFromTimeBetweenPreviousSlot = this.slots.some((slot, index) => {
                        return index !== this.currentModifyingSlotIndex &&
                            moment(this.slotDateFromTime)
                                .isBetween(
                                    moment(slot.datetime),
                                    moment(slot.datetime)
                                        .add(slot.duration, "minutes")
                                            .second(0)
                                            .millisecond(0),
                                    "minute"
                                );
                    });
                    this.slotForm.timeFrom.$setValidity("max", !isDateFromTimeBetweenPreviousSlot);
                }
            }
        );

        $scope.$watch(
            () => this.slotDateToTime,
            () => {
                const isDateToTimeBetweenPreviousSlot = this.slots.some(
                    (slot, index) =>
                        index !== this.currentModifyingSlotIndex &&
                        moment(this.slotDateToTime).isBetween(
                            moment(slot.datetime),
                            moment(slot.datetime).add(slot.duration, "minutes").second(0).millisecond(0),
                            "minute"
                        )
                );
                this.slotForm.timeTo.$setValidity("max", !isDateToTimeBetweenPreviousSlot);
            }
        );
    }

    public $onInit() {
        this.initSlotForm(null);
        if (this.slots.length > 0) {
            this.hideSlotForm();
        }
    }

    public addSlot() {
        if (!this.slotForm.$invalid) {
            const momentDateFrom = moment(this.slotDateFrom)
                .hour(moment(this.slotDateFromTime).get("hour"))
                .minute(moment(this.slotDateFromTime).get("minutes"));
            const slot = {
                datetime: momentDateFrom.format(DATE_FORMAT_ISO),
                duration: moment(this.slotDateToTime).diff(momentDateFrom, "minutes")
            };

            if (this.currentModifyingSlotIndex !== null) {
                this.slots[this.currentModifyingSlotIndex] = slot;
            } else {
                this.slots.push(slot);
            }

            this.hideSlotForm();
        }
    }

    public editSlot(slotSelected) {
        const index = this.slots.findIndex(slot => isEqual(slotSelected, slot));
        this.currentModifyingSlotIndex = index;
        this.initSlotForm(this.slots[index]);
    }

    public hideSlotForm() {
        this.isSlotFormDisplayed = false;
        this.currentModifyingSlotIndex = null;
    }

    public initSlotForm(slot) {
        const MINUTES_IN_HOUR = 60;

        if (!slot) {
            if (this.slots.length > 0) {
                const datetime = this.currentModifyingSlotIndex
                    ? this.slots[this.currentModifyingSlotIndex - 1].datetime
                    : this.slots[this.slots.length - 1].datetime;
                slot = {
                    datetime: moment(datetime)
                        .add(this.slots[this.slots.length - 1].duration, "minutes")
                        .second(0)
                        .millisecond(0),
                    duration: MINUTES_IN_HOUR
                };
            } else {
                slot = {
                    datetime: moment(this.dateFrom).second(0).millisecond(0),
                    duration: MINUTES_IN_HOUR
                };
            }
            this.minSlotDateFrom = moment().toDate();
        }

        this.slotDateFrom = moment(slot.datetime).toDate();
        this.slotDateFromTime = this.slotDateFrom;
        this.slotDateToTime = moment(slot.datetime).add(slot.duration, "minutes").toDate();

        this.isSlotFormDisplayed = true;
    }

    public removeSlot(slotToRemove) {
        const index = this.slots.findIndex(slot => isEqual(slotToRemove, slot));
        this.slots.splice(index, 1);
        this.hideSlotForm();
    }
}
